import { ToCItemData } from '../_components/ToCItem';
import { translate as t } from 'hooks/useTranslations';
import type { DraftManual } from 'views/App/Portal/Policy/_types/manuals/draft';

/**
 * Builds the Table of Contents data structure with numbering for chapters and policies.
 *
 * Numbering follows the same pattern as draftManualHelper:
 * - Chapters: Sequential 1-based index (only enabled chapters are counted)
 * - Policies: chapterNumber * 100 + policyIndex (e.g., 100, 101, 200, 201)
 * - Prefaces: No numbering
 */
export function buildToCData(manual: DraftManual): ToCItemData {
  const prefaceItems: ToCItemData[] = manual.prefaces.map((preface) => ({
    id: preface.id,
    title: preface.title,
    type: 'preface' as const,
    hasChildren: false,
  }));

  // Filter enabled chapters for numbering
  const enabledChapters = manual.chapters.filter((ch) => ch.state !== 'DISABLED');

  const chapterItems: ToCItemData[] = manual.chapters.map((chapter) => {
    // Get chapter number (1-based index of enabled chapters)
    const chapterIndex = enabledChapters.findIndex((ch) => ch.id === chapter.id);
    const chapterNumber = chapterIndex !== -1 ? chapterIndex + 1 : null;
    const chapterTitle = chapterNumber ? `${chapterNumber}: ${chapter.title}` : chapter.title;

    // Filter enabled policies for numbering
    const enabledPolicies = chapter.policies.filter((p) => p.state !== 'DISABLED' && !p.isNew);
    const newPolicies = chapter.policies.filter((p) => p.isNew);
    const sortedPolicies = [...enabledPolicies, ...newPolicies];

    return {
      id: chapter.id,
      title: chapterTitle,
      type: 'chapter' as const,
      hasChildren: sortedPolicies.length > 0,
      children: sortedPolicies.map((policy, policyIndex) => {
        // Get policy number (chapterNumber * 100 + policyIndex)
        const policyNumber = chapterNumber !== null ? chapterNumber * 100 + policyIndex : null;
        const policyTitle = policyNumber !== null ? `${policyNumber}: ${policy.title}` : policy.title;

        return {
          id: policy.id,
          title: policyTitle,
          type: 'policy' as const,
          hasChildren: false,
        };
      }),
    };
  });

  return {
    id: manual.id,
    title: manual.name,
    type: 'manual',
    hasChildren: true,
    children: [
      ...(prefaceItems.length > 0
        ? [
            {
              id: 'prefaces-group',
              title: t('groups.manuals.exclusions.prefaces'),
              type: 'preface' as const,
              hasChildren: true,
              children: prefaceItems,
            },
          ]
        : []),
      ...chapterItems,
    ],
  };
}